import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Eye, Flame, ThumbsUp, ThumbsDown, MessageCircle, Users, Gift } from 'lucide-react';
import dayjs from 'dayjs';
import { fetchStatisticalByUserIdApi } from '../../../api/user-profile/user-profile.api';
import { UserProfileStats } from '../../../models';
import { getDisplayViews } from '../../../utils/view-count';

const fmt = (n: number) => Number(n || 0).toLocaleString('vi-VN');
const num = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

interface Props {
  profile: any;
  listPost: any[];
  leaderboard?: any;
}

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  from: string;
  to: string;
}> = ({ icon, label, value, from, to }) => (
  <div
    className="relative flex h-[120px] flex-col justify-center gap-2 overflow-hidden rounded-[12px] border-[1.5px] border-white/40 px-6 py-5"
    style={{ backgroundImage: `linear-gradient(to bottom, ${from}, ${to})` }}
  >
    <div className="text-[15px] font-medium text-black">{label}</div>
    <div className="text-[26px] font-semibold text-black">{value}</div>
    <div className="pointer-events-none absolute right-4 top-1/2 flex size-12 -translate-y-1/2 items-center justify-center rounded-[14px] bg-white/70 text-black shadow">
      {icon}
    </div>
  </div>
);

const Dashboard: React.FC<Props> = ({ profile, listPost, leaderboard }) => {
  const [stats, setStats] = useState<UserProfileStats | null>(null);

  useEffect(() => {
    if (!profile?.userId) return;
    fetchStatisticalByUserIdApi(profile.userId)
      .then(setStats)
      .catch(() => setStats(null));
  }, [profile?.userId]);

  const posts = listPost || [];

  // Lượt xem = cộng dồn số hiển thị (x10) trên các bài đã tải. Ca donate nhận từ leaderboard.
  const totalViews = useMemo(
    () => posts.reduce((s, p) => s + getDisplayViews(p), 0),
    [posts]
  );
  const totalDonateReceived = useMemo(
    () =>
      (leaderboard?.userRecieveDonates || []).reduce(
        (s: number, d: any) => s + num(d.totalAmount),
        0
      ),
    [leaderboard]
  );

  const totalLike = num(stats?.like);
  const totalComment = num(stats?.comment);

  // Bảng: sắp theo tương tác (like + bình luận) giảm dần để thấy bài hot nhất
  const rankedPosts = useMemo(
    () =>
      [...posts].sort(
        (a, b) =>
          num(b.voteUpCount) + num(b.commentsCount) - (num(a.voteUpCount) + num(a.commentsCount))
      ),
    [posts]
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Thẻ tổng quan */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatCard icon={<FileText size={22} />} label="Bài viết" value={fmt(num(stats?.post))} from="rgba(162,231,79,0.7)" to="rgba(236,245,171,0.7)" />
        <StatCard icon={<Eye size={22} />} label="Lượt xem" value={fmt(totalViews)} from="rgba(139,193,255,0.7)" to="rgba(206,255,254,0.7)" />
        <StatCard icon={<Flame size={22} />} label="Tương tác" value={fmt(totalLike + totalComment)} from="rgba(244,197,175,0.7)" to="rgba(255,235,206,0.7)" />
        <StatCard icon={<ThumbsUp size={22} />} label="Like" value={fmt(totalLike)} from="rgba(191,219,254,0.7)" to="rgba(224,242,254,0.7)" />
        <StatCard icon={<MessageCircle size={22} />} label="Bình luận" value={fmt(totalComment)} from="rgba(233,213,255,0.7)" to="rgba(243,232,255,0.7)" />
        <StatCard icon={<Users size={22} />} label="Bạn bè" value={fmt(num(stats?.newFriend))} from="rgba(254,215,170,0.7)" to="rgba(255,237,213,0.7)" />
        {totalDonateReceived > 0 && (
          <StatCard icon={<Gift size={22} />} label="Ca donate nhận được" value={`${fmt(totalDonateReceived)} Ca`} from="rgba(251,207,232,0.8)" to="rgba(252,231,243,0.8)" />
        )}
      </div>

      {/* Bảng quản lý bài viết */}
      <div className="rounded-[16px] border border-[#DEE0E6] dark:border-slate-700">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#DEE0E6] dark:border-slate-700">
          <h3 className="text-[18px] font-semibold text-[#19052f] dark:text-white">Quản lý bài viết</h3>
          <span className="text-[13px] text-slate-500">Sắp theo tương tác cao nhất · {posts.length} bài gần đây</span>
        </div>
        {rankedPosts.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500">Chưa có bài viết nào</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="text-left text-[13px] text-slate-500">
                  <th className="px-4 py-2 font-medium">Bài viết</th>
                  <th className="px-3 py-2 font-medium text-right"><span className="inline-flex items-center gap-1"><Eye size={14} /> Lượt xem</span></th>
                  <th className="px-3 py-2 font-medium text-right"><span className="inline-flex items-center gap-1"><ThumbsUp size={14} /> Like</span></th>
                  <th className="px-3 py-2 font-medium text-right"><span className="inline-flex items-center gap-1"><ThumbsDown size={14} /> Dislike</span></th>
                  <th className="px-3 py-2 font-medium text-right"><span className="inline-flex items-center gap-1"><MessageCircle size={14} /> Bình luận</span></th>
                  <th className="px-4 py-2 font-medium text-right">Ngày</th>
                </tr>
              </thead>
              <tbody>
                {rankedPosts.map((p) => {
                  const thumb = p.postImageResponses?.[0]?.url;
                  const label = (p.content || p.title || '(Không có nội dung)').toString();
                  return (
                    <tr key={p.id} className="border-t border-[#EEF0F4] dark:border-slate-800">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          {thumb ? (
                            <img src={thumb} alt="" className="size-9 shrink-0 rounded-md object-cover" />
                          ) : (
                            <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-[#EAEFFA] text-[#657384]"><FileText size={16} /></span>
                          )}
                          <span className="line-clamp-1 max-w-[280px] text-[#19052f] dark:text-slate-100">{label}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(getDisplayViews(p))}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(num(p.voteUpCount))}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(num(p.voteDownCount))}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(num(p.commentsCount))}</td>
                      <td className="px-4 py-2.5 text-right text-slate-500 whitespace-nowrap">{p.createdAt ? dayjs(p.createdAt).format('DD/MM/YYYY') : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
